import React, { useMemo, useState } from 'react';
import { Badge, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../shared/api';
import { useCurrency } from '../../shared/CurrencyContext';
import { useConfirm } from '../../shared/ConfirmContext';
import PageSection from '../../components/layout/PageSection';
import PageShell from '../../components/layout/PageShell';
import { CenteredLoader } from '../../components/LoadingSpinner';
import CategoryFilterBoard from './components/CategoryFilterBoard';
import TableSearchBar from './components/TableSearchBar';
import PriorityToggleButton from './components/PriorityToggleButton';
import PriorityReorderTable from './components/PriorityReorderTable';
import InfiniteScrollBox from './components/InfiniteScrollBox';
import { useDebouncedSearch } from './hooks/useDebouncedSearch';
import { useSortOrdering } from './hooks/useSortOrdering';
import { useCategoryFilterOptions } from './hooks/useCategoryFilterOptions';
import { usePriorityReorder } from './hooks/usePriorityReorder';

const asList = (response) => response?.results || response?.items || response || [];

export default function ProductCatalog() {
  const { t } = useTranslation();
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { searchInput, search, handleSearchInputChange, handleSearchSubmit } = useDebouncedSearch();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const { ordering, toggleOrder, getSortClass } = useSortOrdering('priority');

  const categories = useCategoryFilterOptions(['product-categories'], 'warehouses/api/products/categories_all/');

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['products', search, selectedCategory, ordering],
    queryFn: ({ pageParam = 1 }) => {
      const params = new URLSearchParams({ page: String(pageParam), ordering });
      if (search.trim()) params.set('search', search.trim());
      if (selectedCategory !== 'all') params.set('category', selectedCategory);
      return api.getRaw(`warehouses/api/products/?${params}`);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage?.next ? new URL(lastPage.next).searchParams.get('page') : undefined,
  });

  const products = useMemo(() => data?.pages.flatMap(asList) || [], [data]);

  const handleDuplicate = async (product) => {
    try {
      const duplicate = await api.post(`warehouses/api/products/${product.id}/duplicate/`, {});
      queryClient.invalidateQueries({ queryKey: ['products'] });
      navigate(`/warehouses/products/${duplicate.id}?edit=1`);
    } catch (error) {
      console.error('Product duplication failed:', error);
    }
  };

  const handleDelete = async (product) => {
    if (!await confirm(t('warehouses.products.deleteConfirm', { name: product.name }))) return;
    try {
      await api.delete(`warehouses/api/products/${product.id}/`);
      queryClient.invalidateQueries({ queryKey: ['products'] });
    } catch (error) {
      console.error('Product deletion failed:', error);
    }
  };

  const { sortMode, savingOrder, sortItems, enterSortMode, exitSortMode, handleDragEnd } = usePriorityReorder({
    fetchSortItems: async () => {
      const params = new URLSearchParams({ page_size: '500', ordering: 'priority' });
      if (search.trim()) params.set('search', search.trim());
      if (selectedCategory !== 'all') params.set('category', selectedCategory);
      return asList(await api.get(`warehouses/api/products/?${params}`));
    },
    saveOrder: (withUpdatedPriorities) => api.post('warehouses/api/products/bulk_update_priority/', {
      product_priorities: withUpdatedPriorities.map(product => ({ id: product.id, priority: product.priority })),
    }),
    onExit: () => queryClient.invalidateQueries({ queryKey: ['products'] }),
  });

  const pageActions = (
    <>
      <Link to="/warehouses" className="btn btn-outline-secondary">
        <i className="ti ti-arrow-left me-2" />
        {t('common.back')}
      </Link>
      <Link to="/warehouses/products/add" className="btn btn-primary">
        <i className="ti ti-plus me-2" />
        {t('warehouses.products.add')}
      </Link>
    </>
  );

  return (
    <PageShell
      pretitle={t('warehouses.overview')}
      title={t('warehouses.products.catalog')}
      description={t('warehouses.products.catalogDescription')}
      actions={pageActions}
    >
      <PageSection>
        <div className="d-flex flex-column flex-md-row mb-3 gap-2">
          <TableSearchBar value={searchInput} onChange={handleSearchInputChange} onSubmit={handleSearchSubmit} placeholder={t('warehouses.products.search')} submitLabel={t('warehouses.products.searchButton')} />
          <Link to={`/warehouses/products/categories`} className="btn btn-outline-primary">
            {t('warehouses.categories.title')}
          </Link>
          <Link to={`/warehouses/products/types`} className="btn btn-outline-primary">
            {t('warehouses.types.title')}
          </Link>
          <PriorityToggleButton
            active={sortMode}
            saving={savingOrder}
            onClick={sortMode ? exitSortMode : enterSortMode}
            activeLabel={t('warehouses.products.done')}
            inactiveLabel={t('warehouses.products.changePriority')}
          />
        </div>

        <CategoryFilterBoard
          categories={categories}
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
          allLabel={t('warehouses.products.allCategories')}
        />

        {isLoading ? <CenteredLoader /> : sortMode ? (
          <PriorityReorderTable
            items={sortItems}
            droppableId="products-sort"
            onDragEnd={handleDragEnd}
            saving={savingOrder}
            hint={t('warehouses.products.reorderHint')}
            columns={[
              { key: 'name', label: t('warehouses.products.name'), className: 'fw-semibold', render: (product) => product.name },
              { key: 'category_name', label: t('warehouses.categories.title'), render: (product) => product.category_name },
              { key: 'product_type_name', label: t('warehouses.items.type'), render: (product) => product.product_type_name || product.tracking_type || '-' },
              { key: 'total_quantity_owned', label: t('warehouses.products.total'), render: (product) => product.tracking_type === 'CONSUMABLE' ? <span className="text-muted" title={t('warehouses.products.unlimitedStock')}>&#8734;</span> : (product.total_quantity_owned ?? 0) },
              { key: 'default_daily_rate_display', label: t('warehouses.items.dayTariffAmount'), render: (product) => formatCurrency(product.default_daily_rate_display || 0) },
            ]}
          />
        ) : <InfiniteScrollBox hasNextPage={hasNextPage} isFetchingNextPage={isFetchingNextPage} fetchNextPage={fetchNextPage}>
          <Table hover className="table-vcenter mb-0">
            <thead style={{ position: 'sticky', top: 0, background: 'var(--tblr-bg-surface, white)', zIndex: 1, boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}><tr>
              <th><div className={getSortClass('priority')} onClick={() => toggleOrder('priority')}>{t('warehouses.products.priority')}</div></th>
              <th><div className={getSortClass('name')} onClick={() => toggleOrder('name')}>{t('warehouses.products.name')}</div></th>
              <th><div className={getSortClass('category__name')} onClick={() => toggleOrder('category__name')}>{t('warehouses.categories.title')}</div></th>
              <th><div className={getSortClass('product_type__name')} onClick={() => toggleOrder('product_type__name')}>{t('warehouses.items.type')}</div></th>
              <th><div className={getSortClass('total_quantity_owned')} onClick={() => toggleOrder('total_quantity_owned')}>{t('warehouses.products.total')}</div></th>
              <th><div className={getSortClass('default_daily_rate')} onClick={() => toggleOrder('default_daily_rate')}>{t('warehouses.items.dayTariffAmount')}</div></th>
              <th className="w-1">{t('warehouses.products.actions')}</th>
            </tr></thead>
            <tbody>{products.map((product) => <tr key={product.id}>
              <td className="text-muted" style={{ fontSize: '0.8em' }}>{product.priority ?? '-'}</td>
              <td className="fw-semibold"><Link to={`/warehouses/products/${product.id}`} className="text-reset text-decoration-none">{product.name}</Link></td>
              <td>{product.category_name || '-'}</td>
              <td>{product.product_type_name || product.tracking_type || '-'}</td>
              <td>{product.tracking_type === 'CONSUMABLE' ? <span className="text-muted" title={t('warehouses.products.unlimitedStock')}>&#8734;</span> : (product.total_quantity_owned ?? 0)}</td>
              <td>{formatCurrency(product.default_daily_rate_display || 0)}</td>
              <td><div className="btn-group"><Link to={`/warehouses/products/${product.id}`} className="btn btn-sm btn-outline-primary" title={t('warehouses.products.detail')}><i className="ti ti-eye" /></Link><Link to={`/warehouses/products/${product.id}?edit=1`} className="btn btn-sm btn-outline-info" title={t('warehouses.products.edit')}><i className="ti ti-edit" /></Link><button type="button" className="btn btn-sm btn-outline-warning" title={t('warehouses.products.duplicate')} onClick={() => handleDuplicate(product)}><i className="ti ti-copy" /></button><button type="button" className="btn btn-sm btn-outline-danger" title={t('warehouses.products.delete')} onClick={() => handleDelete(product)}><i className="ti ti-trash" /></button></div></td>
            </tr>)}</tbody>
          </Table>
          {products.length === 0 && <div className="text-center text-secondary py-4">{t('warehouses.products.noResults')}</div>}
        </InfiniteScrollBox>}
      </PageSection>
    </PageShell>
  );
}
